/**
 * AC-1479: Queue mutation hooks (`useUpdateQueue` for now; the file is
 * named `*Mutations` so future create/delete additions can land here without
 * renaming).
 *
 * Mirrors the contract documented in
 * `docs/technical/AC-1377-react-query-technical-design.md` sections 2.6
 * (mutation + invalidation) and 5.3 (mutation hook signatures).
 *
 * ## Why a mutation hook
 *
 * The queue settings UI updates display fields, scheduling, circuit-breaker
 * config, etc. The PayloadFieldSelector save flow (AC-1479) sends
 * `{ display_payload_fields: [...] }`; the queue editor sends `{ name,
 * description, ... }`. A single hook keyed on `queueId` invalidates every
 * subscriber to that queue's cache slot AND the all-queues list, so list
 * pages and detail panels both refresh after a mutation.
 *
 * ## queueId at hook level (closure), not mutate-time
 *
 * Queue mutations are always scoped to the same queue for the lifetime of
 * the surface that triggers them - the settings panel, the selector, the
 * histogram-adjacent edit modal. Closing `queueId` in the hook closure
 * keeps call sites clean (`mutate(payload)`) and guarantees the right
 * cache slot is invalidated. This matches `useAgentMutations.ts`.
 *
 * ## Invalidation strategy
 *
 * On success we invalidate:
 *
 *   - `queryKeys.queue(queueId)` - cascades to every hierarchical child key
 *     including `queuePayloadFields(queueId)` and `queuePriorityHistogram(...)`
 *     so the items list (which reads `display_payload_fields` from the queue
 *     detail) and the discovery dropdown both refresh.
 *   - `queryKeys.queues()` - the all-queues list page so renames and
 *     setting changes show up there too.
 *
 * On failure we invalidate nothing - the cache must not be perturbed by a
 * failed write.
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import { Queue, QueueUpdateSettings } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * `useUpdateQueue(queueId)` - mutation hook for `PUT /api/v1/queues/:queueId`.
 *
 * The mutation accepts a partial `QueueUpdateSettings` body. Both
 * `display_payload_fields` (AC-1479 selector) and the existing
 * scheduling / circuit-breaker / rename payloads are valid bodies.
 *
 * @example
 *   const update = useUpdateQueue(42);
 *   await update.mutateAsync({ display_payload_fields: ['domain'] });
 */
export function useUpdateQueue(
  queueId: number | string,
): UseMutationResult<Queue, Error, QueueUpdateSettings> {
  const queryClient = useQueryClient();
  const id = Number(queueId);

  return useMutation<Queue, Error, QueueUpdateSettings>({
    mutationFn: (payload) => apiService.updateQueue(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.queue(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.queues(),
      });
    },
  });
}

export default useUpdateQueue;
